import { Component, OnInit, signal, WritableSignal, inject, Signal, DestroyRef } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { orderBy } from 'lodash-es';
import { BehaviorSubject } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { OrderUtilityService, SalesRepService, UserService } from '../../../../services';
import { OrderControllerService } from '../../../../services/order/order-controller.service';
import { OrderScreen, SourceSystem } from './../../../../models/enums';
import { DivisionSalesRep, OpenOrder, OrderFilter, OrderShipment, OrderShipmentGroup, User } from './../../../../models';
import { OrderShipmentSelection } from '../../models';
import { addMonths } from 'date-fns';
import { Market } from 'src/app/models/market';
import { MatIcon } from '@angular/material/icon';
import { AngularSplitModule } from 'angular-split';
import { ContentLoaderModule } from '@ngneat/content-loader';
import { OrderComponent } from '../../../../shared/components/order/order.component';
import { FiltersComponent } from '../../../../shared/components/filters/filters.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { OverlayModule } from '@angular/cdk/overlay';
import { OrderShipmentDetailsComponent } from '../order-shipment-details/order-shipment-details.component';
import { AccordionModule } from 'ngx-bootstrap/accordion';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { OrderRefreshTimerComponent } from './components/order-refresh-timer/order-refresh-timer.component';
import { OrderViewStatusComponent } from './components/order-view-status/order-view-status.component';
import { OpenOrdersViewComponent } from './components/open-orders-view/open-orders-view.component';
import { OrderShipmentGroupsList } from '../../../../models/order-shipment-groups-list';

@Component({
  selector: 'insight-order-shipments',
  templateUrl: './order-shipments.component.html',
  styleUrls: ['./order-shipments.component.scss'],
  imports: [
    MatIcon,
    AngularSplitModule,
    ContentLoaderModule,
    OrderComponent,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    OverlayModule,
    OpenOrdersViewComponent,
    BsDatepickerModule,
    AccordionModule,
    OrderShipmentDetailsComponent,
    MatInputModule,
    MatButtonModule,
    FiltersComponent,
    OrderRefreshTimerComponent,
    OrderViewStatusComponent
  ]
})
export class OrderShipmentsComponent implements OnInit {
  // what order details are we currently looking at?
  public orderDetailsConfig: WritableSignal<OrderShipmentSelection> = signal<OrderShipmentSelection>(new OrderShipmentSelection());

  public orderScreens = OrderScreen;
  public openOrders = signal<OpenOrder[]>([]);
  public filtersSubject: BehaviorSubject<OrderFilter> = new BehaviorSubject<OrderFilter>(new OrderFilter()); // object passed to insight filter component
  public filteredOrderShipmentGroups = signal<OrderShipmentGroup[]>([]); // how the group view stores/displays things
  public filteredOrders = signal<OrderShipment[]>([]); // how the ship date view stores/displays things
  public showFiltersPanel = signal<boolean>(true);
  public isInitialLoadComplete = signal<boolean>(false);

  public isRetrievingAllOrderData = signal<boolean>(false); // when retrieving all order data
  public isRetrievingSearchData = signal<boolean>(false); // when retrieving data with search field

  public viewTypeForm: UntypedFormGroup;
  public orderSearchForm: UntypedFormGroup; // search type and search text

  public orderView = new UntypedFormControl(); // either show orders by status group, or by ship date

  public defaultAllMarketOption: Market = new Market('all', 'all');
  public currentOrderView = signal<string>('status');
  public availableMarkets: Market[] = []; // for the markets filter
  public availableSalesReps: DivisionSalesRep[] = []; // for the sales rep filter

  public allOrderShipmentGroupsList: OrderShipmentGroupsList = new OrderShipmentGroupsList(); // used for status view type

  private destroyRef = inject(DestroyRef);

  // Injected services
  public orderUtilityService = inject(OrderUtilityService);
  private activeRoute = inject(ActivatedRoute);
  private formBuilder = inject(UntypedFormBuilder);
  private orderControllerService = inject(OrderControllerService);
  private router = inject(Router);
  private salesRepService = inject(SalesRepService);
  private userService = inject(UserService);
  private toastr = inject(ToastrService);

  public user: Signal<User> = toSignal(this.userService.getUser());

  ngOnInit() {
    // keep errors on screen until we click them
    // this affects all toastr messages globally across the application
    this.toastr.toastrConfig.maxOpened = 1;
    this.toastr.toastrConfig.tapToDismiss = false;
    this.toastr.toastrConfig.timeOut = 3000;

    /// SUBSCRIPTIONS
    /////////////////

    this.orderControllerService.timerRefreshedEvent
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        console.log("Timer refreshed event triggered on order shipments page");
        this.isRetrievingAllOrderData.set(true);
        // if there is an order number in the search, just search all orders for order number.
        // otherwise fetch all active shipments in the next 30 days
        const searchFieldConvertedToNumber: number = Number(this.orderSearchForm.controls.searchFormController.value); // can the text field be converted into a number??

        if (searchFieldConvertedToNumber > 0) {
          this.setupComponentAfterDataChange(); // this will make the call to get order number
        } else {
          this.orderControllerService.refreshAllShipmentsOneTime();
        }

        // update the selected order details if we have any
        if (this.orderDetailsConfig()) {
          this.loadNewShipment();
        }
      });

    this.salesRepService.getAllSalesReps()
      .pipe(
        // get distinct list of sales reps since some have multiple entries
        map((salesReps: DivisionSalesRep[]) => {
          const distinctNames = new Set<string>();
          return salesReps.filter(rep => {
            if (distinctNames.has(rep.salesRepName)) {
              return false;
            }
            distinctNames.add(rep.salesRepName);
            return true;
          });
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(salesReps => {
        this.availableSalesReps = salesReps;

        // if our user is a sales rep...try to set the value to our name
        if (this.user()?.permissions.insightSales) {
          const userName: string = this.user().name.trim();
          const foundUserInSalesRepList = this.availableSalesReps.filter(x => x.salesRepName.trim().toLowerCase() === userName.toLowerCase());
          console.log(foundUserInSalesRepList);
          if (foundUserInSalesRepList && foundUserInSalesRepList.length > 0) {
            console.log('Found sales rep... filtering orders to only show theirs:');
            this.viewTypeForm.controls.salesRepFilter.setValue(userName);
          } else {
            console.log('Could not found sales rep... just showing everything');
          }
        }
      });

    // check for filter configuration changes.
    this.filtersSubject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((newConfig: OrderFilter) => {
        console.log('Filter subject changing:', newConfig);

        // if there is no order data to process, no point in updating UI
        // this also helps during initial load when filtersubject emits before data is available
        if (this.allOrderShipmentGroupsList?.orderShipmentGroups?.length > 0) {
          this.setupComponentAfterDataChange();
        }
      });

    // look for changes in search parameter in query string and update form
    this.activeRoute.queryParamMap.
      pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        q => {
          if (q.get('showFiltersPanel')) {
            this.showFiltersPanel.set(q.get('showFiltersPanel') === 'true' ? true : false);
          }
        }
      );

    // set up filters for order view type, markets selected, and sales reps selected
    this.viewTypeForm = this.formBuilder.group({
      orderView: 'status',
      marketFilter: [[this.defaultAllMarketOption]],
      salesRepFilter: 'all'
    });
    this.viewTypeForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((formFilter: { marketFilter: Market[]; orderView: string; salesRepFilter: string }) => {
        this.currentOrderView.set(formFilter.orderView);

        // the market filter (and sales rep above) are in a different form than the regular filter list
        // the filterSubject is the main object that is sent for all the filtering logic
        // we need to update the filterSubject with the market values after we are done with calculations

        // are we adding a filter to the list??
        const addingFilterToList = this.viewTypeForm.controls.marketFilter?.value?.length > this.filtersSubject?.value?.marketFilter?.length;
        const isAllOptionSelectedNow = formFilter.marketFilter.filter(x => x.orgType === 'all').length > 0;
        const isMultipleOptionsSelectedNow = formFilter.marketFilter.length > 1;
        const wasAllOptionSelectedPreviously = this.filtersSubject.value.marketFilter?.includes(this.defaultAllMarketOption);

        if (formFilter.marketFilter.length === 0) {
          // nothing is selected...select the "All" option
          this.viewTypeForm.controls.marketFilter.setValue([this.defaultAllMarketOption]);
          return;
        }

        if (isMultipleOptionsSelectedNow) {
          // we just added the "all" option...and we have other options selected. clear those and just select all
          if (addingFilterToList && wasAllOptionSelectedPreviously === false && isAllOptionSelectedNow) {
            this.viewTypeForm.controls.marketFilter.setValue([this.defaultAllMarketOption]);
            return;
          }

          // we just selected a market...and we already had the All selected. un-select all option
          if (addingFilterToList && wasAllOptionSelectedPreviously && isAllOptionSelectedNow) {
            const marketOptionsWithoutAll = formFilter.marketFilter.filter(x => x.orgType !== 'all');
            this.viewTypeForm.controls.marketFilter.setValue(marketOptionsWithoutAll);
            return;
          }
        }

        this.filtersSubject.value.marketFilter = formFilter.marketFilter;

        // only change sales rep if value has changed
        if (this.filtersSubject.value.salesRepFilter !== formFilter.salesRepFilter) {
          this.filtersSubject.value.salesRepFilter = formFilter.salesRepFilter;
        }

        // update the filter object so it can do all the filter calculations
        this.filtersSubject.next(this.filtersSubject.value);
      });

    // setup search filtering update logic
    this.orderSearchForm = this.formBuilder.group({
      orderSearchTypeControl: 'order-number',
      searchFormController: ''
    });

    this.orderSearchForm.valueChanges
      .pipe(debounceTime(500))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((formFilter: any) => {
        this.navigateRoute({
          'search': formFilter.searchFormController,
          'searchType': formFilter.orderSearchTypeControl
        });
        this.openOrders.set([]); // clear any open orders that might have existed since we are doing a new search

        // only update data if we have orders to process
        // this also helps with initial page load
        if (this.filteredOrders().length > 0 || this.allOrderShipmentGroupsList.orderShipmentGroups.length > 0) {
          this.setupComponentAfterDataChange();
        }
      });

    /// INITIALIZE
    //////////////

    // start grabbing all order data when page loads. It doesn't matter if we are searching or not
    this.orderControllerService.triggerTimerRefreshedEvent();
    this.isRetrievingAllOrderData.set(true);

    // grab search type from query string
    const searchType: string = this.activeRoute.snapshot.queryParamMap.get('searchType');
    const validSearchTypes = ['order-number', 'customer-number', 'portal-guid'];
    if (validSearchTypes.includes(searchType)) {
      this.orderSearchForm.controls.orderSearchTypeControl.setValue(searchType);
    }

    // grab from query string and load shipment details as necessary
    const paramSearchText: string = this.activeRoute.snapshot.queryParamMap.get('search');
    if (paramSearchText !== null) {
      this.orderSearchForm.controls.searchFormController.setValue(paramSearchText);
    }

    const paramShipmentDetailsOrderNum: number = parseInt(this.activeRoute.snapshot.queryParamMap.get('detailsOrderNumber'), 10);
    const paramShipmentDetailsShipmentNum: number = parseInt(this.activeRoute.snapshot.queryParamMap.get('detailsShipmentNumber'), 10);
    const paramShipmentDetailsSourceSystem: number = parseInt(this.activeRoute.snapshot.queryParamMap.get('detailsSourceSystem'), 10);

    const orderDetailsFromQueryString: OrderShipmentSelection = new OrderShipmentSelection();
    if (paramShipmentDetailsOrderNum && paramShipmentDetailsShipmentNum) {
      orderDetailsFromQueryString.orderNumber = paramShipmentDetailsOrderNum;
      orderDetailsFromQueryString.shipmentNumber = paramShipmentDetailsShipmentNum;
      orderDetailsFromQueryString.sourceSystem = paramShipmentDetailsSourceSystem;
      this.orderDetailsConfig.set(orderDetailsFromQueryString);

      this.loadNewShipment();
    }

    this.allOrderShipmentGroupsList = this.orderControllerService.getAllOrderShipments(); // It might already exist without needing an extra API call

    this.orderControllerService.orderDataRefreshedEvent
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.allOrderShipmentGroupsList = this.orderControllerService.getAllOrderShipments();  // current value stored on service
        this.isRetrievingAllOrderData.set(true);

        // get unique list of market codes that have orders and build out market options
        // there is a market orgType and sales division. We need unique between them all,
        // so key the map on the org and sales division concatenated
        const marketsByKey = new Map<string, Market>();
        this.allOrderShipmentGroupsList.orderShipmentGroups.forEach(group => {
          group.orderShipments.forEach(shpment => {
            const marketKey = `${shpment.marketOrgType}|${shpment.marketSalesDivision}`;
            if (!marketsByKey.has(marketKey)) {
              marketsByKey.set(marketKey, new Market(shpment.marketOrgType, shpment.marketSalesDivision));
            }
          });
        });

        this.availableMarkets = Array.from(marketsByKey.values());
        this.availableMarkets.sort((a: Market, b: Market) => this.orderUtilityService.getMarketName(a.orgType) < this.orderUtilityService.getMarketName(b.orgType) ? -1 : 0);

        // since we rebuilt out a list of market options, we lost our previous selection
        // Note: Javascript stores the selection in an array... which is stored by memory address
        // we need to have the selection reference the availableMarkets object so each item points to the right memory address
        const newSelection: Market[] = [];
        this.availableMarkets.forEach(selectionToAdd => {
          const selected = this.filtersSubject.value.marketFilter?.filter(y => y.orgType === selectionToAdd.orgType && y.salesDivision === selectionToAdd.salesDivision);
          if (selected && selected.length > 0) {
            newSelection.push(selectionToAdd);
          }
        });

        // we don't have any selection, so just select everything
        if (newSelection.length === 0) {
          newSelection.push(this.defaultAllMarketOption);
        }

        this.viewTypeForm.controls.marketFilter.setValue(newSelection);

        this.setupComponentAfterDataChange();
      });

    this.orderControllerService.newOrderDetailsAvailableEvent
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        console.log("New order details are available. Reloading.");
        const shipmentToLoad: OrderShipmentSelection = {
          orderNumber: this.orderDetailsConfig().orderNumber,
          shipmentNumber: this.orderDetailsConfig().shipmentNumber,
          sourceSystem: this.orderDetailsConfig().sourceSystem === 0 ? SourceSystem.Sgc : SourceSystem.Adebs
        };

        this.selectOrder(shipmentToLoad);
      });
  }

  public toggleShowingFiltersPanel(): void {
    this.showFiltersPanel.set(!this.showFiltersPanel());

    const showFiltersForQueryParam = this.showFiltersPanel() ? 'true' : 'false';
    this.navigateRoute({ 'showFiltersPanel': showFiltersForQueryParam });
  }

  public selectOrder(data: OrderShipmentSelection): void {
    this.orderDetailsConfig.set(data); // update our current selection since it was manually clicked by the person
    this.loadNewShipment();
  }

  public loadNewShipment(): void {
    if (this.orderDetailsConfig()) {
      // update URL parameters to keep track of what the new details view is
      const queryParams: Params = {
        detailsOrderNumber: this.orderDetailsConfig().orderNumber,
        detailsShipmentNumber: this.orderDetailsConfig().shipmentNumber,
        detailsSourceSystem: this.orderDetailsConfig().sourceSystem
      };

      console.log(`Loading new shipment: (order) ${queryParams.detailsOrderNumber} (shipment) ${queryParams.detailsShipmentNumber} (source system) ${queryParams.detailsSourceSystem}`);

      this.navigateRoute(queryParams);
    }
  }

  public isOrderCurrentlySelected(order: OrderShipment): boolean {
    if (order && this.orderDetailsConfig()) {
      if (order.orderNumber === this.orderDetailsConfig().orderNumber &&
        order.shipmentNumber === this.orderDetailsConfig().shipmentNumber) {
        return true;
      }
    }
    return false;
  }

  public isEmptyGroups(groups: OrderShipmentGroup[]): boolean {
    return groups.every(g => g.orderShipments.length === 0);
  }

  public currentSearchText(): string {
    // the UI uses this to show order number for open lines
    return this.orderSearchForm.controls.searchFormController.value;
  }

  public shouldShowCustomerNumberNotice(): boolean {
    return this.orderSearchForm.controls.orderSearchTypeControl.value === 'customer-number';
  }

  private setupComponentAfterDataChange(): void {
    if (this.allOrderShipmentGroupsList === undefined) { return; }
    if (this.orderSearchForm === undefined) { return; } // our form controls haven't been initialized...so abort

    const areWeSearchingByOrderNumber: boolean = this.orderSearchForm.controls.orderSearchTypeControl.value === 'order-number';
    const areWeSearchingByPortalGuid: boolean = this.orderSearchForm.controls.orderSearchTypeControl.value === 'portal-guid';
    const areWeSearchingByCustomerNumber: boolean = this.orderSearchForm.controls.orderSearchTypeControl.value === 'customer-number';
    const isSearchTextEmpty: boolean = this.orderSearchForm.controls.searchFormController.value === '';

    console.log(`Doing an order search with "${this.orderSearchForm.controls.searchFormController.value}"`);

    // if we are searching by order number...search across all orders...and open orders
    if (areWeSearchingByOrderNumber && !isSearchTextEmpty) {

      // order number must only contain numbers.
      const searchOrderNumber: number = Number(this.orderSearchForm.controls.searchFormController.value);

      if (isNaN(searchOrderNumber)) {
        this.toastr.error('Order number must be a number', 'Invalid Order Number');
        return;
      }

      this.isRetrievingSearchData.set(true); // show loading spinner
      this.orderControllerService.searchAllOrdersByOrderNumber(searchOrderNumber).then((orderShipGroup: OrderShipmentGroup[]) => {
        if (orderShipGroup) {
          this.isRetrievingSearchData.set(false);
          this.filteredOrderShipmentGroups.set(orderShipGroup);
          this.setupComponentAfterDataChangePartTwo();
          return;
        }
      }).catch((exception: any) => {
        this.isRetrievingSearchData.set(false);
        this.toastr.error('There was an error trying to search for the order number. Make sure it is valid', 'Cannot search order number');
      });

      this.orderControllerService.getOpenOrders(searchOrderNumber).then((openOrderResults: OpenOrder[]) => {
        if (openOrderResults) {
          this.openOrders.set(openOrderResults);
          this.setupComponentAfterDataChangePartTwo();
          return;
        }
      });
    } else if (areWeSearchingByPortalGuid && !isSearchTextEmpty) {
      // do portal search
      this.isRetrievingSearchData.set(true); // makes loader spin
      this.orderControllerService.searchAllOrdersByPortalId(this.orderSearchForm.controls.searchFormController.value).then((shipmentGroups: OrderShipmentGroup[]) => {
        this.isRetrievingSearchData.set(false); // stops loader

        // we are only going to show orders from the past 25 months to avoid overloading the UI. Some Portals have 700+ orders
        const orderDateToGoBackTo = addMonths(new Date(), -25);
        shipmentGroups.forEach((shpmentGroup: OrderShipmentGroup) => {
          shpmentGroup.orderShipments = shpmentGroup.orderShipments.filter((shipmnt: OrderShipment) =>
            new Date(shipmnt.shipDate).getTime() > new Date(orderDateToGoBackTo).getTime());
        });
        shipmentGroups = shipmentGroups.filter(value => value.orderShipments.length > 0); // don't keep group if there are no shipments

        this.filteredOrderShipmentGroups.set(shipmentGroups);
        this.setupComponentAfterDataChangePartTwo();
        return;
      });
    } else if (areWeSearchingByCustomerNumber && !isSearchTextEmpty) {
      // customer number has to be a number
      const searchCustomerNumber: number = Number(this.orderSearchForm.controls.searchFormController.value);
      if (isNaN(searchCustomerNumber)) {
        this.toastr.error('Customer number must be a number', 'Invalid Customer Number');
        return;
      }

      this.isRetrievingSearchData.set(true); // makes loader spin
      this.orderControllerService.getOrdersByCustomerNumber(searchCustomerNumber).toPromise().then((shipmentGroups: OrderShipmentGroup[]) => {
        this.isRetrievingSearchData.set(false); // stops loader
        this.filteredOrderShipmentGroups.set(shipmentGroups);
        this.setupComponentAfterDataChangePartTwo();
        return;
      });
    } else {
      // we are using the normal 30 day out order data
      const filteredShipmentGroups = this.orderUtilityService.applyFilters(this.allOrderShipmentGroupsList, this.filtersSubject.value, this.orderSearchForm.controls.searchFormController.value);
      this.filteredOrderShipmentGroups.set(filteredShipmentGroups);
      this.setupComponentAfterDataChangePartTwo();
    }
  }

  private setupComponentAfterDataChangePartTwo(): void {
    // our ship date view data will use the same info as the group by calculations above. We also just need to sort it
    let newFilteredOrders: OrderShipment[] = this.convertFromGroupDataToShipmentData(this.filteredOrderShipmentGroups());
    newFilteredOrders = orderBy(newFilteredOrders, ['shipDate', 'needDate']); // primary and secondary sort
    this.filteredOrders.set(newFilteredOrders);

    // This block of code does the logic for selecting the first element in the filtered results
    // We want to re-select the first item if the filters change
    if (this.filteredOrderShipmentGroups().length > 0 && this.currentOrderView() === 'status') {
      // how to select an element when the view is by status groupings
      const firstShipmentToLoad: OrderShipment = this.filteredOrderShipmentGroups()[0].orderShipments[0];
      const shipmentToLoad: OrderShipmentSelection = {
        orderNumber: firstShipmentToLoad.orderNumber,
        shipmentNumber: firstShipmentToLoad.shipmentNumber,
        sourceSystem: firstShipmentToLoad.sourceSystem === 0 ? SourceSystem.Sgc : SourceSystem.Adebs
      };

      // nothing will be selected when the data comes back for the first time
      if (this.orderDetailsConfig().orderNumber === undefined) {
        this.selectOrder(shipmentToLoad);
      }
    } else if (this.currentOrderView() === 'ship-date' && this.filteredOrders().length > 0) {
      // how to select an element when it is only shipments
      const shipmentToLoad: OrderShipmentSelection = {
        orderNumber: this.filteredOrders()[0].orderNumber,
        shipmentNumber: this.filteredOrders()[0].shipmentNumber,
        sourceSystem: this.filteredOrders()[0].sourceSystem === 0 ? SourceSystem.Sgc : SourceSystem.Adebs
      };

      if (this.orderDetailsConfig().orderNumber === undefined) {
        this.selectOrder(shipmentToLoad);
      }
    } else {
      // no data returned
      this.loadNewShipment();
    }

    this.isRetrievingAllOrderData.set(false);
    if (this.isInitialLoadComplete() === false && this.allOrderShipmentGroupsList.cacheProcessingTime !== null) {
      this.isInitialLoadComplete.set(true);
    }
  }

  private navigateRoute(queryParams: Params): void {
    this.router.navigate([], {
      relativeTo: this.activeRoute,
      replaceUrl: false,
      queryParams,
      queryParamsHandling: 'merge'
    });
  }

  private convertFromGroupDataToShipmentData(shipmentGroups: OrderShipmentGroup[]): OrderShipment[] {
    // also store data in normal order shipments in case we want to use that view
    const tempAllOrderShipments: OrderShipment[] = [];
    shipmentGroups.forEach(group => {
      group.orderShipments.forEach(shipment => {
        tempAllOrderShipments.push(shipment);
      });
    });
    tempAllOrderShipments.sort((a, b) => a.shipDate < b.shipDate ? -1 : 0);
    return tempAllOrderShipments;
  }
}
